import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Clock, MapPin, BadgeCheck, FileText, CalendarClock,
  HelpCircle, Car, Bike, Truck, Bus, GraduationCap, Users, Gauge, Caravan, MessageCircle, Zap,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { whatsappLink } from "@/lib/faro";
import { useSeo, JsonLd, SITE_URL, ogFromAsset } from "@/lib/seo";
import { PERMIT_LANDINGS } from "@/data/permitLandings";
import PictureImg from "@/components/PictureImg";

const ICONS = {
  clock: Clock, badge: BadgeCheck, file: FileText, calendar: CalendarClock,
  car: Car, bike: Bike, truck: Truck, bus: Bus, cap: GraduationCap,
  users: Users, gauge: Gauge, caravan: Caravan, map: MapPin,
};

const AULA = "Centro Comercial 3 Plaza América, locales 12, 13 y 14, 36211 Vigo";

// Renders **bold** segments inside plain strings
function Rich({ text }) {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return parts.map((seg, i) => (i % 2 === 1 ? <strong key={i}>{seg}</strong> : <React.Fragment key={i}>{seg}</React.Fragment>));
}

export default function PermitLandingPage({ slug }) {
  const d = PERMIT_LANDINGS[slug];
  const wa = whatsappLink("34658790266", d.waMessage);

  useSeo({ title: d.title, description: d.description, canonical: `${SITE_URL}/${slug}`, ogImage: ogFromAsset(d.images?.[0]?.src), ogImageAlt: d.images?.[0]?.alt });

  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid={`landing-${slug}`}>
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Course",
        name: d.schemaName,
        description: d.description,
        provider: { "@type": ["LocalBusiness", "EducationalOrganization"], "@id": `${SITE_URL}/autoescuela-plaza-america#business`, name: "Autoescuelas Faro", url: SITE_URL },
        hasCourseInstance: { "@type": "CourseInstance", courseMode: "Onsite" },
        locationCreated: { "@type": "Place", name: "Autoescuelas Faro - Plaza América", address: AULA },
        url: `${SITE_URL}/${slug}`,
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: d.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Permisos y carnets", item: `${SITE_URL}/permisos` },
            { "@type": "ListItem", position: 3, name: d.h1, item: `${SITE_URL}/${slug}` },
          ],
        }} />

      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)]" data-testid="pl-back">
          <ArrowLeft size={16} /> Volver al inicio
        </Link>
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <span className="eyebrow">{d.eyebrow}</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">{d.h1}</h1>
        {d.intro.map((p) => (
          <p key={p.slice(0, 40)} className="mt-4 text-lg text-[var(--faro-text-soft)]"><Rich text={p} /></p>
        ))}

        <div className="mt-6 flex flex-wrap gap-3">
          <a href={wa} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-sm" data-testid="pl-cta-presupuesto">
            <MessageCircle size={15} /> Solicita presupuesto
          </a>
          <a href="/#contacto" className="btn-pill btn-outline text-sm" data-testid="pl-cta-matricula">
            <CalendarClock size={15} /> Matricúlate
          </a>
        </div>

        {(slug === "carnet-coche-vigo" || slug === "carnet-moto-vigo") && (
          <Link
            to="/curso-intensivo-teorica-vigo"
            data-testid="pl-intensivo-strip"
            className="mt-6 flex items-center justify-between gap-3 rounded-2xl bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] p-4 sm:p-5 hover:brightness-95 transition"
          >
            <div className="flex items-center gap-3">
              <Zap size={22} className="text-[var(--faro-red)] shrink-0" />
              <div>
                <div className="font-display font-black leading-tight">¿Con prisa? Teórico en 1 semana</div>
                <div className="text-xs opacity-80">Curso intensivo de teórica, presencial u online a tu medida</div>
              </div>
            </div>
            <ArrowRight size={18} className="shrink-0" />
          </Link>
        )}

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {d.stats.map((f) => {
            const Icon = ICONS[f.icon] || BadgeCheck;
            return (
              <div key={f.k} className="rounded-2xl bg-[var(--faro-blue)] text-white p-4">
                <Icon size={18} className="text-[var(--faro-yellow)]" />
                <div className="font-display font-black text-2xl mt-2">{f.k}</div>
                <div className="text-xs text-white/80 mt-0.5 leading-snug">{f.v}</div>
              </div>
            );
          })}
        </div>

        {d.images?.length > 0 && (
          <div className={`mt-6 grid gap-4 ${d.images.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`} data-testid="pl-photos">
            {d.images.map((im, i) => (
              <figure key={im.src} className="rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)] bg-white">
                <PictureImg
                  src={im.src}
                  alt={im.alt}
                  width={800}
                  height={600}
                  priority={i === 0}
                  sizes="(min-width: 640px) 50vw, 100vw"
                  className={`w-full ${im.fit === "contain" ? "object-contain bg-[var(--faro-bg)] max-h-[480px]" : `object-cover ${d.images.length === 1 ? "h-72 sm:h-96" : "h-56 sm:h-64"}`}`}
                />
                {im.caption && (
                  <figcaption className="px-4 py-2.5 text-sm font-semibold text-[var(--faro-text-soft)]">{im.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        )}

        {d.sections.map((s) => (
          <section className="mt-10" key={s.h2}>
            <h2 className="font-display font-black text-2xl sm:text-3xl">{s.h2}</h2>
            {(s.paras || []).map((p, pi) => (
              <p key={`${pi}-${p.slice(0, 32)}`} className="mt-3 text-[var(--faro-text-soft)]"><Rich text={p} /></p>
            ))}
            {s.bullets && (
              <ul className="mt-3 space-y-2 text-[var(--faro-text-soft)]">
                {s.bullets.map((b, bi) => (
                  <li key={`${bi}-${b.slice(0, 32)}`} className="flex gap-2">
                    <BadgeCheck size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" />
                    <span><Rich text={b} /></span>
                  </li>
                ))}
              </ul>
            )}
            {s.cards && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {s.cards.map((c) => (
                  <div key={c.title} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
                    <div className="font-display font-black text-lg text-[var(--faro-blue-deep)]">{c.title}</div>
                    <p className="mt-2 text-sm text-[var(--faro-text-soft)]"><Rich text={c.text} /></p>
                    {c.bullets && (
                      <ul className="mt-3 space-y-1.5">
                        {c.bullets.map((b, cbi) => (
                          <li key={`${cbi}-${b.slice(0, 32)}`} className="text-sm flex items-start gap-2 text-[var(--faro-text)]">
                            <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[var(--faro-red)] shrink-0" />
                            <span><Rich text={b} /></span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {c.link && (
                      <Link to={c.link.to} className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)] transition-colors" data-testid={`pl-card-link${c.link.to.replace(/\//g, "-")}`}>
                        {c.link.label} <ArrowRight size={13} />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
            {s.image && (
              <figure className="mt-5 rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)] bg-white">
                <img src={s.image.src} alt={s.image.alt} width={s.image.width} height={s.image.height} loading="lazy" decoding="async" className="w-full h-auto" />
                {s.image.caption && (
                  <figcaption className="px-4 py-2.5 text-sm font-semibold text-[var(--faro-text-soft)]">{s.image.caption}</figcaption>
                )}
              </figure>
            )}
          </section>
        ))}

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Precio: solicita presupuesto sin compromiso</h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            Cada alumno es distinto: preparamos un <strong>presupuesto a tu medida</strong> según el permiso, la modalidad de
            teórica y las clases prácticas que necesites. Pídelo sin compromiso por{" "}
            <a href={wa} target="_blank" rel="noreferrer" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">WhatsApp (658 790 266)</a>{" "}
            o llamando al <a href="tel:+34986292455" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">986 29 24 55</a>. Te respondemos el mismo día.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Dónde puedes matricularte</h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            Puedes matricularte y preparar la teórica en cualquiera de nuestros <strong>5 centros</strong>:{" "}
            <Link to="/autoescuela-plaza-america" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Plaza América (Vigo)</Link>,{" "}
            <Link to="/autoescuela-coruxo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Coruxo</Link>,{" "}
            <Link to="/autoescuela-panxon" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Panxón</Link>,{" "}
            <Link to="/autoescuela-nigran" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">Nigrán</Link> y{" "}
            <Link to="/autoescuela-a-caniza" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">A Cañiza</Link>.
            {" "}{d.centersNote}
          </p>
        </section>

        <section className="mt-10" id="faq">
          <h2 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2">
            <HelpCircle className="text-[var(--faro-red)]" size={26} /> Preguntas frecuentes
          </h2>
          <div className="mt-4 space-y-2" data-testid="pl-faq">
            {d.faq.map((f) => (
              <details key={f.q} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] px-5 py-4 group">
                <summary className="cursor-pointer list-none font-bold text-[var(--faro-blue-deep)] flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[var(--faro-red)] group-open:rotate-90 transition-transform shrink-0"><ArrowRight size={16} /></span>
                </summary>
                <p className="mt-2 text-sm text-[var(--faro-text-soft)]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {d.related?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display font-black text-xl">También te puede interesar</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {d.related.map((r) => (
                <Link key={r.to} to={r.to} className="btn-pill btn-ghost text-sm" data-testid={`pl-related-${r.to.replace(/\//g, "")}`}>
                  {r.label} <ArrowRight size={14} />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 rounded-2xl bg-[var(--faro-blue)] text-white p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-yellow)]">Más de 45 años formando conductores</div>
            <div className="font-display font-black text-xl mt-1">{d.ctaTitle}</div>
            <p className="text-sm text-white/85 mt-1">{d.ctaText}</p>
          </div>
          <div className="flex flex-col gap-2">
            <a href={wa} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-sm justify-center" data-testid="pl-cta-final">
              <MessageCircle size={14} /> Solicita presupuesto
            </a>
            <a href="/#contacto" className="btn-pill bg-white text-[var(--faro-blue)] text-sm justify-center">
              Matricúlate <ArrowRight size={14} />
            </a>
          </div>
        </div>

        <p className="mt-8 text-sm text-[var(--faro-text-soft)]" data-testid="pl-cambios-dgt-link">
          Recuerda que el examen teórico se actualiza con la normativa vigente: consulta las{" "}
          <Link to="/blog/cambios-dgt-1-octubre-2026" className="font-bold text-[var(--faro-blue)] underline underline-offset-2">
            nuevas normas de circulación del 1 de octubre de 2026
          </Link>{" "}
          para estudiar con material al día.
        </p>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
